import { Address } from "../common/Address.interface";
import { Vehicle } from "./Vehicle.interface";
import { Driver } from "./Driver.interface";
import { Company } from "./Company.interface";
import { DossierMedia } from "./DossierMedia.interface";
import { AppointmentTimeSlot } from "./AppointmentTimeSlot.interface";
import { ServicePartner } from "./ServicePartner.interface";
import { DossierUsageEmailSent } from "./EmailSent.interface";
import { DossierUsageSmsSent } from "./SmsSent.interface";
import { ImageCaptureSent } from "./ImageCaptureSent.interface";
import { DwProSent } from "./DwProSent.interface";
import { TranslatableEnum } from "./TranslatableEnum.interface";
import { Recourse } from "./Recourse.interface";

export interface EvaluationRuleSet {
  id: string;
  name: string;
  priority: number;
  hideInAdmin: boolean;
}

interface DamageClaimableRule extends EvaluationRuleBase {
  type: "associative_array_value";
  output: { damageClaimable?: boolean };
}
interface ProcessAllowedRule extends EvaluationRuleBase {
  type: "process_allowed";
  output: {
    processAllowed: "required" | "optional" | "not_allowed";
    viewConfiguration: string | null;
    servicePartner?: string;
  };
}

interface ServicePartnerCollectionRule extends EvaluationRuleBase {
  type: "service_partner_collection";
  output: { servicePartnerCollection: string | null };
}

export interface EvaluationRuleBase {
  id: string;
  name: string;
  ruleSet: EvaluationRuleSet;
}
export type EvaluationRule =
  | DamageClaimableRule
  | ProcessAllowedRule
  | ServicePartnerCollectionRule;

export interface FleetLeaseLabel {
  displayName: string;
}

export interface FleetClient {
  displayName: string | null;
  externalReference: string | null;
}

export interface FleetRecord {
  fleetLeaseLabel: FleetLeaseLabel | null;
  fleetClient: FleetClient | null;
  leaseContractEndAt: string | null;
  externalInsured: boolean | null;
}

export interface OwnerAffiliate {
  id: string;
  name: string;
}

export interface BaseDossier {
  "@context": string;
  "@id": string;
  "@type": string;
  id: string;
  dossierType: string;
  createdAt: string;
  vehicleHandInLocationName: string | null;
  vehicleHandInLocationAddress: Address | null;
  causeOfDamage: string | null | TranslatableEnum;
  typeOfDamage: string | null | TranslatableEnum;
  damageCountry: string | null;
  damageAt: string | null;
  imageCapture: boolean | null;
  damageFromDirection: string | null | TranslatableEnum;
  driverType: string | null | TranslatableEnum;
  paymentByType: string | null | TranslatableEnum;
  insurerName: string | null;
  mandate: string | null;
  polisNumber: string | null;
  damageNumber: string | null;
  driveable: boolean | null;
  /** Indicates whether the vehicle is requested to be repaired. */
  repairRequested: boolean | null;
  towedAway: boolean | null;
  damageReported: boolean | null;
  pickupService: boolean | null;
  replacementVehicle: boolean | null;
  replacementVehicleType: string | null | TranslatableEnum;
  commentFromDriver: string | null;
  additionalComment: string | null;
  insuranceType: string | null | TranslatableEnum;
  serviceAdvisorName: string | null;
  serviceAdvisorEmail: string | null;
  status: string | null;
  otherPartyInvolved:
    | "known_other_party"
    | "unknown_other_party"
    | "no_other_party"
    | null
    | TranslatableEnum;
  isDeviatingDriver: boolean | null;
  kilometers: number | null;
  vehicle: Vehicle;
  driver: Driver;
  mediaUsages: DossierMedia[];
  hasAttachablePhotos?: boolean;
  appointmentTimeSlots: AppointmentTimeSlot[];
  appointmentTimeSlotsPlanned: AppointmentTimeSlot[];
  servicePartners: ServicePartner[];
  leaseCompany: Company;
  insuranceCompany: Company;
  dealershipCompany: Company;
  dossierUsageEmailSentDriver: DossierUsageEmailSent<"EmailSentDriver">[];
  dossierUsageEmailSentServicePartner: DossierUsageEmailSent<"EmailSentServicePartner">[];
  dossierUsageEmailSentDealership: DossierUsageEmailSent<"EmailSentDealership">[];
  dossierUsageEmailSentAffiliate: DossierUsageEmailSent<"EmailSentAffiliate">[];
  dossierUsageSmsSentDriver: DossierUsageSmsSent<"SmsSentDriver">[];
  dossierUsageDwProSent: DwProSent[]; //TODO: Define type
  imageCaptureSents: ImageCaptureSent[]; //TODO: Define type
  recourse: Recourse | null;
  expectedFinishDateTime: string | null;
  source: string | null;
  fleetRecord: FleetRecord | null;
  matchedRules: EvaluationRule[];
  ownerAffiliate: OwnerAffiliate | null;
  adminFirstReadAt: string | null;
}

export interface RepairDirectGlassDossier extends BaseDossier {
  dossierType: "repair_direct.glass";
  glassDamageLocation: TranslatableEnum | null;
  glassDamageType: TranslatableEnum | null;
  glassJobType: TranslatableEnum | null;
  numberOfDamagePoints: number | null;
  damageSizeLargerThanCoin: boolean | null;
  damageNearEdge: boolean | null;
  damageInDriverVision: boolean | null;
}

export interface RepairDirectGenericDossier extends BaseDossier {
  dossierType: "repair_direct.generic";
  damageClaimable: boolean | null;
  damagedParty:
    | "both_parties"
    | "just_me"
    | "other_party"
    | null
    | TranslatableEnum;
  responsibleParty:
    | "other_party"
    | "driver"
    | "unknown"
    | null
    | TranslatableEnum;
  personalInjury: boolean | null;
  hasPoliceReport: boolean | null;
  repairType: string | null | TranslatableEnum;
}

export interface OtherDossier extends BaseDossier {
  dossierType: string & {};
}

export type Dossier =
  | RepairDirectGlassDossier
  | RepairDirectGenericDossier
  | OtherDossier;

export function isRepairDirectGlassDossier(
  dossier: Dossier,
): dossier is RepairDirectGlassDossier {
  return dossier.dossierType === "repair_direct.glass";
}

export function isRepairDirectGenericDossier(
  dossier: Dossier,
): dossier is RepairDirectGenericDossier {
  return dossier.dossierType === "repair_direct.generic";
}
